import { memo, useState } from 'react';
import { Copy, ExternalLink, Info, Layers, Play, Share2, Star } from 'lucide-react';
import { TechniqueStatus } from '../types';
import { getThumbnailUrl, getThumbnailFallbackUrl } from '../utils/video';
import { shareTechnique } from '../utils/share';
import { isMobile } from './PipPlayer';

interface TechniqueTileProps {
  id: string;
  name: string;
  status: TechniqueStatus;
  categoryColor: string;
  instagramUrl?: string;
  thumbnail?: string;
  watched?: boolean;
  /** How many clips this tile stands in for. >1 collapses it into a group:
   *  tapping opens the version chooser, and the per-clip action buttons move
   *  to the chooser's rows. */
  variantCount?: number;
  onVariantsClick?: () => void;
  /** Desktop-only: open every version in the group as a grid of PiP players. */
  onOpenAllVersions?: () => void;
  onPipClick?: (url: string, name: string) => void;
  /** Toggle this technique on/off the user's mat (practice queue). */
  onToggleMat?: () => void;
  /** Open the technique details modal. */
  onInfoClick?: () => void;
}

// Grid-view counterpart to TechniqueCard: a poster tile with the name over the
// bottom of the image. Deliberately not draggable — dragging is a list-view
// affordance (drop a card to spawn a PiP), and a grid of drag handles fights
// with scrolling on touch.
function TechniqueTile({ id, name, status, categoryColor, instagramUrl, thumbnail, watched, variantCount, onVariantsClick, onOpenAllVersions, onPipClick, onToggleMat, onInfoClick }: TechniqueTileProps) {
  const preferred = getThumbnailUrl(instagramUrl, thumbnail);
  // Thumbnails are remote (Storage / img.youtube.com) and can 404 — step down
  // to the next candidate, then to the same tinted panel used when there's
  // no thumbnail at all.
  const [dead, setDead] = useState<string[]>([]);
  let src = preferred;
  while (src && dead.includes(src)) src = getThumbnailFallbackUrl(src);
  const mobile = isMobile();
  // Same rule as the list card: a tile standing in for several clips opens the
  // chooser rather than playing one of them behind the user's back.
  const grouped = (variantCount ?? 1) > 1 && !!onVariantsClick;


  return (
    <div
      data-id={id}
      className={`technique-tile${watched ? ' technique-tile--watched' : ''}`}
      title={grouped ? `${name} — ${variantCount} versions` : (instagramUrl ? 'Watch video' : name)}
      onClick={grouped ? onVariantsClick : (instagramUrl && onPipClick ? () => onPipClick(instagramUrl, name) : undefined)}
    >
      {src ? (
        <img
          className="technique-tile-img"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setDead((d) => [...d, src!])}
        />
      ) : (
        <div className="technique-tile-fallback" style={{ background: categoryColor }} />
      )}

      {/* Grouped tiles carry the same top-right bar a single tile uses, with
          the version count standing where the star would and "open all"
          beside it — the two things a group can do. */}
      {grouped && (
        <div className="technique-tile-actions technique-tile-actions--group" onClick={(e) => e.stopPropagation()}>
          {/* The bar swallows clicks so its buttons don't play the tile;
              the count is itself the way into the chooser. */}
          <button
            type="button"
            className="technique-tile-versions"
            onClick={onVariantsClick}
            aria-label={`Choose one of ${variantCount} versions`}
            title={`${variantCount} versions — choose one`}
          >
            <Layers size={11} />
            {variantCount}
          </button>
          {/* Desktop only, by the same test the handler uses — see TechniqueCard. */}
          {onOpenAllVersions && !mobile && (
            <button
              className="technique-tile-btn technique-tile-playall"
              onClick={onOpenAllVersions}
              aria-label={`Open all ${variantCount} versions in picture-in-picture`}
              title="Open all videos"
            >
              <ExternalLink size={13} />
            </button>
          )}
        </div>
      )}

      {!grouped && (
      <div className="technique-tile-actions" onClick={(e) => e.stopPropagation()}>
        {onToggleMat && (
          <button
            className={`technique-tile-btn${status === TechniqueStatus.MarkedForPractice ? ' technique-tile-btn--on' : ''}`}
            onClick={onToggleMat}
            aria-label={status === TechniqueStatus.MarkedForPractice ? 'Remove from Favorites' : 'Add to Favorites'}
          >
            <Star size={13} fill={status === TechniqueStatus.MarkedForPractice ? 'currentColor' : 'none'} />
          </button>
        )}
        <button
          className="technique-tile-btn"
          onClick={() => shareTechnique(id, name, mobile)}
          aria-label={mobile ? 'Share technique' : 'Copy link to technique'}
          title={mobile ? 'Share this technique' : 'Copy link to this technique'}
        >
          {mobile ? <Share2 size={13} /> : <Copy size={13} />}
        </button>
        {onInfoClick && (
          <button className="technique-tile-btn" onClick={onInfoClick} aria-label="Technique details">
            <Info size={13} />
          </button>
        )}
      </div>
      )}

      {/* Tier colour rides on the play glyph rather than a separate stripe —
          a bar per tile reads as a seam in a gapless grid. */}
      <span className="technique-tile-name">
        {grouped
          ? <Layers className="technique-tile-glyph" size={9} style={{ color: categoryColor }} />
          : instagramUrl && <Play className="technique-tile-glyph" size={9} fill="currentColor" style={{ color: categoryColor }} />}
        {name}
      </span>
    </div>
  );
}

export default memo(TechniqueTile);
